<script setup lang="ts">
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import { UiLogo, UiText, UiCard } from '@shopexperts/shared';
import { useValidateReviewToken } from '../composables/reviews/useValidateReviewToken';
import SubmitPublicReviewForm from '../components/reviews/SubmitPublicReviewForm.vue';

const route = useRoute();
const token = computed(() => route.query.token as string | undefined);

// Validate token
const { isLoading, isValid, expertData } = useValidateReviewToken(token);
</script>

<template>
    <div class="bg-[#efedff] min-h-screen w-full relative overflow-x-hidden">
        <div class="flex items-start justify-center px-4 py-10 md:py-20">
            <!-- Loading State -->
            <UiCard v-if="isLoading" padding="lg" class="w-full max-w-[550px]">
                <div class="flex flex-col items-center justify-center gap-4 py-8">
                    <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-se-primary"></div>
                    <UiText as="p" variant="p" class="text-se-gray-600">
                        Validating review link...
                    </UiText>
                </div>
            </UiCard>

            <!-- Invalid Token State -->
            <UiCard v-else-if="!isValid" padding="lg" class="w-full max-w-[550px]">
                <div class="flex flex-col items-center justify-center gap-4 py-8">
                    <svg class="w-16 h-16 text-se-danger" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12">
                        </path>
                    </svg>
                    <UiText as="p" variant="h3" class="text-se-gray-900 font-semibold text-center">
                        Invalid Review Link
                    </UiText>
                    <UiText as="p" variant="p" class="text-se-gray-600 text-center">
                        This review link is no longer valid. Please contact the expert who sent you this link.
                    </UiText>
                </div>
            </UiCard>

            <!-- Valid Token - Show Form -->
            <UiCard v-else-if="isValid && expertData && token" padding="lg" class="w-full max-w-[550px]">
                <div class="flex flex-col gap-6">
                    <div class="h-6 relative">
                        <UiLogo size="md" />
                    </div>
                    
                    <SubmitPublicReviewForm
                        :token="token"
                        :expert="expertData"
                        :show-close-button="false"
                    />
                </div>
            </UiCard>
        </div>
    </div>
</template>
